/* eslint-disable camelcase */

import {Record} from 'immutable'
import {trackpointMetrics} from 'lib/metricsCalculator'

// Canonical deserialization formats for rwgps data. The idea here is not to
// extends the functionality of plain JS objects, just to enforce key naming,
// immutability, and defaults for deserialized objects coming in from rails.

// In some special cases like trips, we do expensive processing at the time
// of deserialization.

// Usage guidelines:
// - Components that accept these data types MUST NOT error when provided a
//   record with all properties set to the default.
// - Components that accept these data types SHOULD gain access to them through
//   redux LOAD_* actions, rather than including deserialization in the
//   component

// Trip with track points
export const Trip = Record({
  id: null,
  name: null,
  description: null,
  photos: [],
  departedAt: null,
  trackPoints: [],
  coursePoints: [],
  rawPoints: [],
  elevationGain: null,
  elevationLoss: null,
  distance: null,
  movingTime: null,
  postalCode: null,
  privacyCode: null,
  locality: null,
  administrativeArea: null,
  isStationary: null,
  createdAt: null,
  user: null,
  gear: null,
  boundingBox: null,
  metrics: null,
  highlightedPhotoId: null,
  _permissions: {},
  movingTimeSeconds: null
}, 'TripRecord')

export const deserializeTrip = (trip) => {
  const {
    administrative_area,
    bounding_box,
    departed_at,
    elevation_gain,
    elevation_loss,
    first_lat,
    first_lng,
    moving_time,
    photos,
    privacy_code,
    track_points,
    highlighted_photo_id,
    ...props
  } = trip

  const trackPoints = track_points ? trackpointMetrics(track_points) : null // eslint-disable-line

  return new Trip({
    ...props,
    departedAt: departed_at,
    elevationGain: elevation_gain,
    elevationLoss: elevation_loss,
    movingTime: (typeof moving_time === 'number') ? moving_time : 0,
    privacyCode: privacy_code,
    trackPoints: trackPoints,
    rawPoints: track_points,
    photos: photos ? photos.map(deserializePhoto) : [],
    boundingBox: bounding_box,
    administrativeArea: administrative_area,
    highlightedPhotoId: highlighted_photo_id
  })
}

// Trip summary without track points
export const TripSummary = Record({
  id: null,
  name: null,
  description: null,
  photos: []
}, 'TripSummaryRecord')

export const deserializeTripSummary = (props) => new TripSummary(props)

export const Route = Record({
  id: null,
  name: null,
  description: null,
  defaultBasemap: 'rwgps',
  visibility: 0,
  photos: [],
  trackPoints: [],
  coursePoints: [],
  rawPoints: [],
  pointsOfInterest: [],
  elevationGain: null,
  elevationLoss: null,
  recreationTypes: [],
  pavementType: null,
  distance: null,
  postalCode: null,
  privacyCode: null,
  locality: null,
  administrativeArea: null,
  country: null,
  createdAt: null,
  updatedAt: null,
  user: null,
  event: null,
  club: null,
  boundingBox: null,
  metrics: null,
  pinned: false,
  segments: [],
  mapPdf: null,
  highlightedPhotoId: null,
  _permissions: {},
  hasCoursePoints: null,
  views: 0,
  metricUnits: null,
  copiedFromName: null,
  copiedFromPath: null,
  bylineName: null,
  bylinePath: null,
  locked: null
}, 'RouteRecord')

// this is a trashy hack
const userIsLoggedIn = window.rwgps && window.rwgps.config.currentUser
export const deserializeRoute = ({
  track_points,
  course_points,
  created_at,
  updated_at,
  points_of_interest,
  default_basemap,
  photos,
  bounding_box,
  elevation_gain,
  elevation_loss,
  rememberable,
  recreation_type_ids,
  pavement_type_id,
  segment_matches,
  administrative_area,
  privacy_code,
  highlighted_photo_id,
  moving_time_seconds,
  has_course_points,
  metric_units,
  copied_from_name,
  copied_from_path,
  views,
  byline_name,
  byline_path,
  ...props}) => {
  const pinned = userIsLoggedIn && (rememberable === false)
  return new Route({
    ...props,
    createdAt: created_at,
    updatedAt: updated_at,
    defaultBasemap: default_basemap,
    coursePoints: course_points,
    privacyCode: privacy_code,
    trackPoints: track_points ? trackpointMetrics(track_points) : null, // eslint-disable-line
    elevationGain: elevation_gain,
    elevationLoss: elevation_loss,
    rawPoints: track_points,
    pointsOfInterest: points_of_interest,
    photos: photos ? photos.map(deserializePhoto) : [],
    boundingBox: bounding_box,
    recreationTypes: recreation_type_ids,
    pavementType: pavement_type_id,
    pinned: pinned,
    segments: segment_matches,
    administrativeArea: administrative_area,
    highlightedPhotoId: highlighted_photo_id,
    hasCoursePoints: has_course_points,
    views: views || 0,
    metricUnits: metric_units,
    copiedFromName: copied_from_name,
    copiedFromPath: copied_from_path,
    bylineName: byline_name,
    bylinePath: byline_path
  })
}

export const Organization = Record({
  id: null,
  name: null,
  subhead: null,
  description: null,
  userId: null,
  subscriptionId: null,
  createdAt: null,
  logo: null,
  cover: null,
  inviteOnly: false,
  messagesEnabled: true,
  calendarEnabled: true,
  memberMessagesEnabled: true,
  active: true,
  requireNameAndEmail: false,
  organizationType: 'organization',
  contactName: null,
  contactEmail: null,
  richDescription: null,
  externalUrls: null,
  ambassadorId: null,
  locality: null,
  administrativeArea: null,
  slug: null
}, 'OrganizationRecord')

export const deserializeOrganization = ({
  user_id,
  subscription_id,
  created_at,
  invite_only,
  message_enabled,
  calendar_enabled,
  member_messages_enabled,
  require_name_and_email,
  organization_type,
  contact_name,
  contact_email,
  rich_description,
  external_urls,
  ambassador_id,
  administrative_area,
  ...props}) => (
  new Organization({
    ...props,
    userId: user_id,
    subscriptionId: subscription_id,
    createdAt: created_at,
    inviteOnly: invite_only,
    messageEnabled: message_enabled,
    calendarEnabled: calendar_enabled,
    memberMessagesEnabled: member_messages_enabled,
    requireNameAndEmail: require_name_and_email,
    organizationType: organization_type,
    contactName: contact_name,
    contactEmail: contact_email,
    richDescription: rich_description ? JSON.parse(rich_description) : null, // eslint-disable-line
    externalUrls: external_urls,
    ambassadorId: ambassador_id,
    administrativeArea: administrative_area
  })
)

export const Photo = Record({
  id: null,
  userId: null,
  caption: null,
  lat: null,
  lng: null,
  capturedAt: null,
  createdAt: null,
  updatedAt: null,
  width: null,
  height: null
})

export const deserializePhoto = (photo) => {
  const {captured_at, created_at, updated_at, user_id, ...props} = photo

  return new Photo({
    ...props,
    userId: user_id,
    createdAt: created_at,
    updatedAt: updated_at,
    capturedAt: captured_at
  })
}

export const PointOfInterest = Record({
  id: null, // integer
  typeId: 17, // integer - "Generic"
  parentId: null, // integer
  parentType: null, // string
  userId: null, // integer
  name: null, // string
  description: null, // string
  url: null, // string
  lat: null, // float
  lng: null, // float
  tagNames: [], // Array of strings
  photoIds: [], // Array of ints
  createdAt: null, // datetime
  updatedAt: null // datetime
})

export const deserializePointOfInterest = (poi) => {
  const {created_at, updated_at, poi_type, user_id, tag_names, photo_ids, parent_id, parent_type, ...props} = poi

  return new PointOfInterest({
    ...props,
    typeId: poi_type,
    parentId: parent_id,
    parentType: parent_type,
    userId: user_id,
    tagNames: tag_names,
    photoIds: photo_ids || [], // backend sends null instead of empty array if no photoset exists
    createdAt: created_at,
    updatedAt: updated_at
  })
}

export const Notification = Record({
  id: null, // integer
  title: null, // string
  richTitle: null, // string
  lockscreenHint: null, // string
  image: null, // idk
  imageTarget: {}, // object
  textTarget: {}, // object
  ctaPrimary: {}, // object
  ctaSecondary: {}, // object
  updatedAt: null, // date string
  readStatus: null, // probably just 'seen' or 'unseen'
  archived: false // boolean
})

export const deserializeNotification = (notification) => {
  const {rich_title, lockscreen_hint, image_target, text_target, cta_primary, cta_secondary, updated_at, read_status, ...props} = notification

  return new Notification({
    ...props,
    richTitle: rich_title,
    lockscreenHint: lockscreen_hint,
    imageTarget: image_target,
    textTarget: text_target,
    ctaPrimary: cta_primary,
    ctaSecondary: cta_secondary,
    updatedAt: updated_at,
    readStatus: read_status
  })
}

export * from './FindResult'
export * from './Segment'
export * from './EmbedData'
export * from './Experience'
export * from './Favorite'
export * from './User'
export * from './GuidePage'
